import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Dialog, Flex, Heading, Select, Separator, Switch, Text, TextField } from "@radix-ui/themes";
import { useQueryClient } from "@tanstack/react-query";

import { useProfileContext } from "context";
import {
  useGetEatAppStatus,
  useSaveEatAppMappings,
  useUpdateEatAppPullSettings,
  useValidateEatApp,
} from "hooks";
import { useToast } from "../../../context/ToastContext";
import { EatAppLocationMappingFields, RESTAURANT_REQUIRED } from "./EatAppLocationMappingFields";

const inputStyle = { width: "100%" };

export function EatAppSetupDialog({ isOpen, onOpenChange, onConnected, existingConfig = null }) {
  const queryClient = useQueryClient();
  const { setToastMessage } = useToast();
  const { userProfile } = useProfileContext();
  const organisationId = userProfile?.organisation_id;
  const { data: statusData } = useGetEatAppStatus(organisationId);
  const validate = useValidateEatApp();
  const saveMappings = useSaveEatAppMappings();
  const updatePullSettings = useUpdateEatAppPullSettings();

  const [step, setStep] = useState(1);
  const [apiKey, setApiKey] = useState("");
  const [groupId, setGroupId] = useState("");
  const [environment, setEnvironment] = useState("sandbox");
  const [configId, setConfigId] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [mappings, setMappings] = useState({});
  const [pullEnabled, setPullEnabled] = useState(false);
  const [reconnectRequired, setReconnectRequired] = useState(false);

  useEffect(() => {
    if (isOpen) return;
    setStep(1);
    setApiKey("");
    setGroupId("");
    setEnvironment("sandbox");
    setConfigId(null);
    setRestaurants([]);
    setMappings({});
    setPullEnabled(false);
    setReconnectRequired(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !existingConfig) return;
    const details = existingConfig;
    setGroupId(details.group_id || "");
    setEnvironment(details.environment || "sandbox");
    setPullEnabled(Boolean(details.pull_enabled));
    setMappings(Object.fromEntries(
      (details.location_mappings || []).map((mapping) => [
        mapping.bconnect_location_id,
        mapping.restaurant_id,
      ])
    ));
  }, [isOpen, existingConfig]);

  const locations = useMemo(
    () => statusData?.data?.available_locations || [],
    [statusData]
  );
  const selectedMappings = useMemo(() => (
    locations
      .filter((location) => mappings[location.id] && mappings[location.id] !== RESTAURANT_REQUIRED)
      .map((location) => ({
        bconnect_location_id: location.id,
        restaurant_id: mappings[location.id],
      }))
  ), [locations, mappings]);
  const hasIncompleteMappings = locations.some((location) => mappings[location.id] === RESTAURANT_REQUIRED);

  const connect = async (actionChoice = null) => {
    if (!apiKey.trim() || !groupId.trim()) {
      setToastMessage("Enter the API key and Concierge group ID.");
      return;
    }
    try {
      const body = {
        api_key: apiKey.trim(),
        group_id: groupId.trim(),
        environment,
        organisation_id: organisationId,
      };
      if (actionChoice) body.action_choice = actionChoice;
      const response = await validate.mutateAsync(body);
      if (response.data.status === "pending_user_choice") {
        setReconnectRequired(true);
        setRestaurants(response.data.restaurants || []);
        return;
      }
      setConfigId(response.data.config_id);
      setRestaurants(response.data.restaurants || []);
      setReconnectRequired(false);
      setStep(2);
    } catch (error) {
      setToastMessage(error?.response?.data?.message || "Eat App credentials could not be validated.");
    }
  };

  const activate = async () => {
    if (hasIncompleteMappings) {
      setToastMessage("Choose an Eat App restaurant for every assigned location.");
      return;
    }
    if (!selectedMappings.length) {
      setToastMessage("Map at least one BConnect location to an Eat App restaurant.");
      return;
    }
    const otherConfigs = (statusData?.data?.configs || []).filter(
      (config) => String(config.config_id) !== String(configId)
    );
    const selectedIds = new Set(selectedMappings.map((mapping) => String(mapping.bconnect_location_id)));
    const movedCount = otherConfigs.flatMap((config) => config.location_mappings || [])
      .filter((mapping) => selectedIds.has(String(mapping.bconnect_location_id))).length;
    if (movedCount && !window.confirm(
      `${movedCount} location(s) will move from another Eat App group. Continue?`
    )) return;
    try {
      await saveMappings.mutateAsync({
        organisation_id: organisationId,
        config_id: configId,
        location_mappings: selectedMappings,
      });
      await updatePullSettings.mutateAsync({
        organisation_id: organisationId,
        config_id: configId,
        pull_enabled: Boolean(pullEnabled),
      });
      queryClient.invalidateQueries({ queryKey: ["eat-app-status"] });
      setToastMessage("Eat App connected successfully.");
      onOpenChange(false);
      onConnected?.();
    } catch (error) {
      setToastMessage(error?.response?.data?.message || "Eat App setup could not be saved.");
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
      <Dialog.Content maxWidth="680px" style={{ maxHeight: "85vh", overflowY: "auto" }}>
        <Dialog.Title>{existingConfig ? "Update Eat App connection" : "Connect Eat App group"}</Dialog.Title>
        <Dialog.Description size="2" mb="4">
          Synchronize guest profiles between BConnect and your Eat App restaurants.
        </Dialog.Description>
        <Flex gap="2" wrap="wrap" mb="4">
          {["Connection", "Restaurant mapping", "Review"].map((label, index) => (
            <Badge key={label} color={step === index + 1 ? "green" : "gray"} variant="soft">
              {index + 1}. {label}
            </Badge>
          ))}
        </Flex>
        <Separator size="4" mb="4" />

      {step === 1 && (
        <Card size="3">
          <Flex direction="column" gap="5">
            <Heading size="5">Connect your Concierge group</Heading>
            <Text color="gray">Use the long-term Concierge API key supplied by Eat App. One key provides access to one group.</Text>
            <label>
              <Text as="div" mb="2" weight="medium">Environment</Text>
              <Select.Root value={environment} onValueChange={setEnvironment}>
                <Select.Trigger variant="surface" style={{ width: "100%" }} />
                <Select.Content position="popper">
                  <Select.Item value="sandbox">Sandbox</Select.Item>
                  <Select.Item value="production">Production</Select.Item>
                </Select.Content>
              </Select.Root>
            </label>
            <label>
              <Text as="div" mb="2" weight="medium">Concierge group ID</Text>
              <TextField.Root value={groupId} onChange={(event) => setGroupId(event.target.value)} placeholder="Enter the Concierge group UUID" style={inputStyle} />
            </label>
            <label>
              <Text as="div" mb="2" weight="medium">Concierge API key</Text>
              <TextField.Root type="password" value={apiKey} onChange={(event) => setApiKey(event.target.value)} placeholder="Paste the API key supplied by Eat App" style={inputStyle} />
            </label>

            {reconnectRequired && (
              <Card variant="surface">
                <Flex direction="column" gap="3">
                  <Text weight="medium">A previous connection was found. How should synchronization continue?</Text>
                  <Button onClick={() => connect("resume")}>Resume and sync missed data</Button>
                  <Button variant="soft" onClick={() => connect("continue_from_now")}>Start from now and skip missed data</Button>
                  <Button color="orange" variant="soft" onClick={() => connect("sync_all_historical")}>Sync all historical guests</Button>
                </Flex>
              </Card>
            )}

            {!reconnectRequired && (
              <Button onClick={() => connect()} loading={validate.isPending}>Validate and continue</Button>
            )}
          </Flex>
        </Card>
      )}

      {step === 2 && (
        <Card size="3">
          <Flex direction="column" gap="5">
            <Heading size="5">Map brands and locations</Heading>
            <Text color="gray">Assign this Eat App group to a brand, then choose the matching restaurant for each location.</Text>
            <EatAppLocationMappingFields
              organisationId={organisationId}
              configId={configId}
              locations={locations}
              restaurants={restaurants}
              mappings={mappings}
              setMappings={setMappings}
              configs={statusData?.data?.configs || []}
            />
            <Flex justify="between">
              <Button variant="soft" color="gray" onClick={() => setStep(1)}>Back</Button>
              <Button onClick={() => setStep(3)} disabled={!selectedMappings.length || hasIncompleteMappings}>Continue</Button>
            </Flex>
          </Flex>
        </Card>
      )}

      {step === 3 && (
        <Card size="3">
          <Flex direction="column" gap="5">
            <Heading size="5">Review data synchronization</Heading>
            <Card variant="surface">
              <Flex justify="between" align="center">
                <Flex direction="column" gap="1">
                  <Text weight="medium">BConnect → Eat App guest profiles</Text>
                  <Text size="2" color="gray">Always enabled for the selected locations.</Text>
                </Flex>
                <Badge color="green">Enabled</Badge>
              </Flex>
            </Card>
            <Card variant="surface">
              <Flex justify="between" align="center">
                <Flex direction="column" gap="1">
                  <Text weight="medium">Eat App → BConnect guest profiles</Text>
                  <Text size="2" color="gray">Import guests created directly in Eat App.</Text>
                </Flex>
                <Switch checked={pullEnabled} onCheckedChange={setPullEnabled} />
              </Flex>
            </Card>
            <Text size="2" color="gray">{selectedMappings.length} location mapping(s) will be activated.</Text>
            <Flex justify="between">
              <Button variant="soft" color="gray" onClick={() => setStep(2)}>Back</Button>
              <Button onClick={activate} loading={saveMappings.isPending}>Connect Eat App</Button>
            </Flex>
          </Flex>
        </Card>
      )}
        <Flex justify="end" mt="4">
          <Button variant="soft" color="gray" onClick={() => onOpenChange(false)}>Cancel</Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
